import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BellOff, Bell, X, Sunrise, Moon, Timer, Infinity as InfinityIcon, BookOpen, Download } from "lucide-react";
import { toast } from "sonner";
import { haptic } from "../utils/haptic";
import { nextFocusStart } from "../notifications/notificationService";

/**
 * FocusStatusChip — header pill that is ALWAYS visible so the user can
 * flip Focus on / off from any surface without hunting through
 * Settings. Two visual states:
 *
 *   OFF (dim / grey `Bell` icon) — single-tap opens a menu with
 *     30m · 1h · 2h · Until sunrise · Turn ON (indefinite). Picking
 *     any option calls `onActivate` with the appropriate settings
 *     patch so the parent can persist to IndexedDB.
 *
 *   ON (indigo `BellOff` icon) — shows the reason (manual / timer /
 *     scheduled) plus a live "X left" countdown that ticks every 30s.
 *     X button calls `onCancel` which the parent uses to clear the
 *     manual toggle + Focus-Now timer in one shot. Schedule keeps
 *     working. Tapping the label itself is a no-op (by design — the
 *     X is the only way to kill Focus so accidental cancels are hard).
 *
 * Props:
 *   status     { active, manual, until, scheduleActive }
 *   onActivate (patch)  → merge settings patch (focus_mode / focus_until)
 *   onCancel   ()       → clear manual + timer
 *   location   optional  { latitude, longitude } for accurate sunrise
 */

function nextSunriseFallback(now = new Date()) {
  const target = new Date(now);
  target.setHours(6, 0, 0, 0);
  if (target.getTime() <= now.getTime()) target.setDate(target.getDate() + 1);
  return target.getTime();
}

async function fetchNextSunrise(location) {
  if (!location || !Number.isFinite(location.latitude) || !Number.isFinite(location.longitude)) {
    return nextSunriseFallback();
  }
  try {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${location.latitude}&longitude=${location.longitude}&daily=sunrise&timezone=auto&forecast_days=2`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Sunrise fetch HTTP ${res.status}`);
    const data = await res.json();
    const items = data?.daily?.sunrise;
    if (Array.isArray(items)) {
      const now = Date.now();
      for (const s of items) {
        const t = new Date(s).getTime();
        if (Number.isFinite(t) && t > now) return t;
      }
    }
  } catch { /* fall through */ }
  return nextSunriseFallback();
}

export default function FocusStatusChip({ status, onActivate, onCancel, location, focusSchedule }) {
  const [, setTick] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const wrapRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    // Tick once a minute whenever there's a live countdown to render —
    // either Focus is ACTIVE (existing behaviour: "3h 12m left") OR
    // Focus is OFF but a schedule is enabled and a next-start exists
    // ("Focus · in 2h 04m") so the OFF-state countdown decrements in
    // real time. Ticks every 30 s to keep minute rollovers snappy.
    const hasScheduledCountdown = !status?.active && !!focusSchedule && !!focusSchedule.enabled;
    if (!status?.active && !hasScheduledCountdown) return undefined;
    const iv = setInterval(() => setTick((t) => t + 1), 30 * 1000);
    return () => clearInterval(iv);
  }, [status?.active, focusSchedule]);

  // Close the OFF-state menu on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    // Re-anchor the fixed menu to the chip's current viewport rect so
    // it sits flush under the pill. Handles scroll / resize while open.
    const anchor = () => {
      const el = btnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setMenuPos({
        top: Math.round(r.bottom + 4),
        right: Math.round(window.innerWidth - r.right),
      });
    };
    anchor();
    const onDown = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      // Portaled menu lives outside wrapRef — treat clicks on it as inside.
      if (e.target && e.target.closest && e.target.closest('[data-focus-menu-portal="1"]')) return;
      setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", anchor);
    window.addEventListener("scroll", anchor, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", anchor);
      window.removeEventListener("scroll", anchor, true);
    };
  }, [menuOpen]);

  const active = !!status?.active;

  // -------- OFF STATE: dim pill + tap menu ------------------------------
  if (!active) {
    const pick = async (kind) => {
      setMenuOpen(false);
      if (!onActivate) return;
      haptic("milestone");
      if (kind === "manual") {
        onActivate({ focus_mode: true, focus_until: 0 });
        return;
      }
      if (kind === "sunrise") {
        const ts = await fetchNextSunrise(location);
        onActivate({ focus_mode: false, focus_until: ts });
        return;
      }
      if (kind === "nightly") {
        // Reuse the EXISTING recurring nightly schedule stored on
        // `settings.focus_schedule` (see FocusModeSchedule.jsx). We do
        // NOT create a new scheduler here — we simply flip the
        // existing schedule's master `enabled` flag to `true` so its
        // configured per-day windows (which correctly handle
        // overnight ranges like 22:00 → 06:30 via isInFocusWindow)
        // take effect. If the user hasn't configured any enabled
        // days yet, we bail out with a clear toast rather than
        // silently activating an empty schedule.
        const sched = focusSchedule || null;
        const days = sched?.days || {};
        const anyDayEnabled = Object.values(days).some((d) => d && d.enabled);
        if (!sched || !anyDayEnabled) {
          toast.info("Configure a nightly schedule first", {
            description: "Open Settings → Focus Mode → Schedule Focus Mode to set your nightly hours.",
            duration: 5000,
          });
          return;
        }
        if (sched.enabled) {
          toast.success("Nightly schedule is already active", {
            description: "Follows your saved Focus Mode → Schedule Focus Mode times.",
            duration: 4000,
          });
          return;
        }
        onActivate({ focus_schedule: { ...sched, enabled: true } });
        toast.success("Nightly schedule enabled", {
          description: "Focus will follow your saved schedule (edit in Settings).",
          duration: 4000,
        });
        return;
      }
      const map = { "30m": 30 * 60 * 1000, "1h": 60 * 60 * 1000, "2h": 2 * 60 * 60 * 1000 };
      const ms = map[kind];
      if (ms) onActivate({ focus_mode: false, focus_until: Date.now() + ms });
    };

    // OFF-state label — if a nightly schedule is enabled AND its next
    // start is within the next 24 hours, show "Focus · in Xh Ym" so
    // users see the countdown at a glance. Otherwise fall back to
    // "Focus · off". Recomputes every 30 s via the countdown tick above.
    const nextStartTs = nextFocusStart(new Date(), focusSchedule);
    const withinDay = nextStartTs && (nextStartTs - Date.now()) <= 24 * 60 * 60 * 1000;
    let offLabel = "Focus · off";
    let offTitle = "Focus Mode is OFF — tap to silence alarms";
    if (withinDay) {
      const msLeft = Math.max(0, nextStartTs - Date.now());
      const mins = Math.max(0, Math.round(msLeft / 60000));
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      const dur = h === 0 ? `${m}m` : (m === 0 ? `${h}h` : `${h}h ${m}m`);
      offLabel = `Focus · in ${dur}`;
      const startDate = new Date(nextStartTs);
      const startTxt = startDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      offTitle = `Next scheduled Focus window starts at ${startTxt} — tap to silence alarms sooner`;
    }

    return (
      <div ref={wrapRef} className="relative inline-flex mt-1 shrink-0">
        <button
          ref={btnRef}
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/[0.06] text-slate-300 border border-white/15 backdrop-blur-sm hover:bg-white/[0.12] hover:text-white transition-colors"
          data-testid="focus-status-chip"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          title={offTitle}
        >
          <Bell className="w-3 h-3" strokeWidth={2.4} />
          <span>{offLabel}</span>
        </button>

        {menuOpen && typeof document !== "undefined" && createPortal(
          <div
            role="menu"
            ref={(el) => {
              // Outside-click needs to know about the portaled node too
              // (wrapRef.contains would miss it since it lives in body).
              // We stash it as a data attr and check both refs on click.
              if (el) el.setAttribute("data-focus-menu-portal", "1");
            }}
            style={{
              position: "fixed",
              top: menuPos.top,
              right: menuPos.right,
              zIndex: 2147483000,
              backgroundColor: "rgb(15 23 42)", // slate-900 opaque
            }}
            className="min-w-[160px] rounded-lg border border-white/15 shadow-2xl py-1"
            data-testid="focus-status-menu"
          >
            <MenuRow icon={<Timer className="w-3.5 h-3.5" />} label="30 minutes" onClick={() => pick("30m")} testid="focus-status-menu-30m" />
            <MenuRow icon={<Timer className="w-3.5 h-3.5" />} label="1 hour" onClick={() => pick("1h")} testid="focus-status-menu-1h" />
            <MenuRow icon={<Timer className="w-3.5 h-3.5" />} label="2 hours" onClick={() => pick("2h")} testid="focus-status-menu-2h" />
            <MenuRow icon={<Sunrise className="w-3.5 h-3.5" />} label="Until sunrise" onClick={() => pick("sunrise")} testid="focus-status-menu-sunrise" />
            <MenuRow icon={<Moon className="w-3.5 h-3.5" />} label="Nightly" onClick={() => pick("nightly")} testid="focus-status-menu-nightly" />
            <div className="h-px bg-white/10 my-1" />
            <MenuRow icon={<InfinityIcon className="w-3.5 h-3.5" />} label="Turn ON (indefinite)" onClick={() => pick("manual")} testid="focus-status-menu-manual" />
            <div className="h-px bg-white/10 my-1" />
            <MenuRow
              icon={<BookOpen className="w-3.5 h-3.5" />}
              label="How Focus works"
              onClick={() => {
                setMenuOpen(false);
                window.open("/focus-mode-guide.html", "_blank", "noopener");
              }}
              testid="focus-status-menu-guide"
            />
            <MenuRow
              icon={<Download className="w-3.5 h-3.5" />}
              label="Download guide (ZIP)"
              onClick={() => {
                setMenuOpen(false);
                // Trigger a browser download of the packaged offline guide.
                const a = document.createElement("a");
                a.href = "/focus-mode-guide.zip";
                a.download = "iron-rabbit-focus-mode-guide.zip";
                document.body.appendChild(a);
                a.click();
                a.remove();
              }}
              testid="focus-status-menu-guide-zip"
            />
          </div>,
          document.body
        )}
      </div>
    );
  }

  // -------- ON STATE: indigo pill + X cancel (unchanged behavior) -------
  let label = "Focus active";
  if (status.until && Date.now() < status.until) {
    const ms = status.until - Date.now();
    const mins = Math.max(1, Math.round(ms / 60000));
    if (mins < 60) label = `Focus · ${mins}m left`;
    else {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      label = m === 0 ? `Focus · ${h}h left` : `Focus · ${h}h ${m}m left`;
    }
  } else if (status.manual) {
    label = "Focus · ON";
  } else if (status.scheduleActive) {
    label = "Focus · scheduled";
  }

  return (
    <div
      className="inline-flex items-center gap-1.5 px-2 py-0.5 mt-1 rounded-full text-[10px] font-medium bg-indigo-500/25 text-indigo-100 border border-indigo-400/40 backdrop-blur-sm shrink-0"
      data-testid="focus-status-chip"
      aria-live="polite"
      title="Focus Mode is silencing alarm popups, sound and haptics"
    >
      <BellOff className="w-3 h-3" strokeWidth={2.4} />
      <span>{label}</span>
      {onCancel && (status.manual || (status.until && Date.now() < status.until)) && (
        <button
          type="button"
          onClick={onCancel}
          className="ml-0.5 -mr-1 p-0.5 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Cancel Focus Mode"
          data-testid="focus-status-cancel"
          title="Cancel Focus Mode now"
        >
          <X className="w-3 h-3" strokeWidth={2.4} />
        </button>
      )}
    </div>
  );
}

function MenuRow({ icon, label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="menuitem"
      data-testid={testid}
      className="w-full text-left px-3 py-1.5 text-xs text-slate-200 hover:bg-white/10 inline-flex items-center gap-2 transition-colors"
    >
      <span className="text-slate-400">{icon}</span>
      <span>{label}</span>
    </button>
  );
}
